import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import {
	ResponsiveContainer,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	PieChart,
	Pie,
	Cell,
} from 'recharts';
import { useCharts } from '../hooks/useCharts';
import type { ChartsData } from '../hooks/useCharts';
import styles from './ChartsPage.module.css';

// ── Palette ───────────────────────────────────────────────────────────────────
const PALETTE = [
	'#3b82f6',
	'#10b981',
	'#f59e0b',
	'#ef4444',
	'#8b5cf6',
	'#06b6d4',
	'#f97316',
	'#84cc16',
	'#ec4899',
	'#14b8a6',
	'#6366f1',
	'#eab308',
	'#22c55e',
	'#a855f7',
	'#0ea5e9',
];
function color(projects: string[], name: string) {
	return PALETTE[projects.indexOf(name) % PALETTE.length];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
/*
   Format a Date as YYYY-MM-DD using LOCAL date components.
   Avoids the UTC shift that toISOString() introduces for users east of
   UTC when the time-of-day is close to local midnight.
*/
function fmtLocal(d: Date): string {
	const vY = d.getFullYear(); // local year
	const vM = String(d.getMonth() + 1).padStart(2, '0'); // local month, 1-based
	const vD = String(d.getDate()).padStart(2, '0'); // local day-of-month
	return `${vY}-${vM}-${vD}`;
}
function todayStr() {
	return fmtLocal(new Date());
}
function threeMonthsAgo() {
	const d = new Date();
	d.setMonth(d.getMonth() - 3);
	return fmtLocal(d);
}
function fmtWeek(iso: string) {
	const d = new Date(iso + 'T12:00:00Z');
	return d.toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' });
}
function fmtD(n: number) {
	if (n === 0) return '0';
	return parseFloat(n.toFixed(2)).toString();
}

// ── Data transforms ───────────────────────────────────────────────────────────
function personLoadData(rows: ChartsData['byPersonProject']) {
	const map = new Map<string, Record<string, number>>();
	for (const r of rows) {
		if (!map.has(r.initials)) map.set(r.initials, {});
		map.get(r.initials)![r.projectName] = r.hours / 8;
	}
	return [...map.keys()].sort().map((ini) => ({ initials: ini, ...map.get(ini) }));
}

function projectDonutData(rows: ChartsData['byPersonProject']) {
	const map = new Map<string, number>();
	for (const r of rows) map.set(r.projectName, (map.get(r.projectName) ?? 0) + r.hours / 8);
	return [...map.entries()].map(([name, days]) => ({ name, days })).sort((a, b) => b.days - a.days);
}

function weeklyData(rows: ChartsData['byWeekProject']) {
	const map = new Map<string, Record<string, number>>();
	for (const r of rows) {
		if (!map.has(r.weekStart)) map.set(r.weekStart, {});
		map.get(r.weekStart)![r.projectName] = r.hours / 8;
	}
	return [...map.keys()].sort().map((w) => ({ week: w, ...map.get(w) }));
}

function heatData(rows: ChartsData['byPersonWeek']) {
	const personsSet = new Set<string>();
	const weeksSet = new Set<string>();
	const matrix = new Map<string, number>();
	for (const r of rows) {
		personsSet.add(r.initials);
		weeksSet.add(r.weekStart);
		matrix.set(`${r.initials}:${r.weekStart}`, r.hours / 8);
	}
	return {
		persons: [...personsSet].sort(),
		weeks: [...weeksSet].sort(),
		matrix,
	};
}

// ── Heat cell color ───────────────────────────────────────────────────────────
function heatBg(d: number): string {
	if (d === 0) return '#f5f5f4';
	if (d < 0.5) return '#dbeafe';
	if (d < 1) return '#bfdbfe';
	if (d < 2) return '#93c5fd';
	if (d < 3) return '#3b82f6';
	if (d < 4) return '#1d4ed8';
	return '#1e3a8a';
}
function heatFg(d: number) {
	return d >= 3 ? '#fff' : '#171717';
}

// ── Project filter ──────────────────────────────────────────────────────────
/*
   Return a copy of the charts data restricted to a single project. An empty
   project name means "all projects" and the data is returned unchanged.
   byPersonWeek has no project dimension, so it cannot be narrowed and is
   passed through as-is.
*/
function filterByProject(inData: ChartsData, inProject: string): ChartsData {
	if (!inProject) return inData;
	return {
		byPersonProject: inData.byPersonProject.filter((r) => r.projectName === inProject),
		byWeekProject: inData.byWeekProject.filter((r) => r.projectName === inProject),
		byPersonWeek: inData.byPersonWeek,
	};
}

// ── Custom tooltip label ──────────────────────────────────────────────────────
const dayFmt = (v: unknown) => [`${fmtD(Number(v))} j`, ''] as [string, string];

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ data }: { data: ChartsData }) {
	const allProjects = useMemo(
		() => [...new Set(data.byPersonProject.map((r) => r.projectName))].sort(),
		[data.byPersonProject],
	);

	const pLoad = useMemo(() => personLoadData(data.byPersonProject), [data.byPersonProject]);
	const pDonut = useMemo(() => projectDonutData(data.byPersonProject), [data.byPersonProject]);
	const pWeekly = useMemo(() => weeklyData(data.byWeekProject), [data.byWeekProject]);

	// Ref + measured height of the "Répartition par projet" box. A trailing
	// spacer of the same height extends the scrollable area downwards so the
	// last panels can be scrolled clear of the viewport bottom.
	const donutRef = useRef<HTMLDivElement>(null); // the "Répartition par projet" panel
	const [spacerH, setSpacerH] = useState(0); // measured donut-box height, in px

	useLayoutEffect(() => {
		if (donutRef.current) setSpacerH(donutRef.current.offsetHeight);
	}, [pLoad, pDonut, pWeekly]);

	if (!pLoad.length) return <p className={styles.empty}>Aucune donnée pour cette période.</p>;

	const barH = Math.max(200, pLoad.length * 48);

	return (
		<div className={styles.dashboard}>
			{/* ── Panel 1: person load ── */}
			<div className={styles.panel}>
				<div className={styles.panelTitle}>Charge par personne</div>
				<ResponsiveContainer width="100%" height={barH}>
					<BarChart layout="vertical" data={pLoad} margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
						<CartesianGrid strokeDasharray="3 3" horizontal={false} />
						<XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${fmtD(v)} j`} />
						<YAxis type="category" dataKey="initials" tick={{ fontSize: 11 }} width={30} />
						<Tooltip formatter={dayFmt} />
						<Legend iconSize={8} wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
						{allProjects.map((proj) => (
							<Bar key={proj} dataKey={proj} stackId="s" fill={color(allProjects, proj)} name={proj} />
						))}
					</BarChart>
				</ResponsiveContainer>
			</div>

			<div className={styles.row2}>
				{/* ── Panel 2: project donut ── */}
				<div ref={donutRef} className={`${styles.panel} ${styles.donutPanel}`}>
					<div className={styles.panelTitle}>Répartition par projet</div>
					<ResponsiveContainer width="100%" height={240}>
						<PieChart>
							<Pie
								data={pDonut}
								dataKey="days"
								nameKey="name"
								innerRadius={60}
								outerRadius={95}
								paddingAngle={2}
							>
								{pDonut.map((entry) => (
									<Cell key={entry.name} fill={color(allProjects, entry.name)} />
								))}
							</Pie>
							<Tooltip
								formatter={(v: unknown, name: unknown) => [`${fmtD(Number(v))} j`, String(name)]}
							/>
						</PieChart>
					</ResponsiveContainer>
				</div>

				{/* ── Panel 3: weekly stacked bars ── */}
				<div className={`${styles.panel} ${styles.weeklyPanel}`}>
					<div className={styles.panelTitle}>Charge hebdomadaire</div>
					<ResponsiveContainer width="100%" height={240}>
						<BarChart data={pWeekly} margin={{ top: 4, right: 16, left: 8, bottom: 44 }}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis
								dataKey="week"
								tick={{ fontSize: 10 }}
								tickFormatter={fmtWeek}
								angle={-45}
								textAnchor="end"
								interval={0}
							/>
							<YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} j`} />
							<Tooltip labelFormatter={(label) => fmtWeek(String(label))} formatter={dayFmt} />
							{allProjects.map((proj) => (
								<Bar
									key={proj}
									dataKey={proj}
									stackId="s"
									fill={color(allProjects, proj)}
									name={proj}
								/>
							))}
						</BarChart>
					</ResponsiveContainer>
				</div>
			</div>

			{/* Trailing white space — height matches the "Répartition par projet" box */}
			<div style={{ height: spacerH, flexShrink: 0 }} aria-hidden="true" />
		</div>
	);
}

// ── Heat Matrix ───────────────────────────────────────────────────────────────
const LEGEND_STEPS = [
	{ label: '0', days: 0 },
	{ label: '< 0.5', days: 0.3 },
	{ label: '0.5–1', days: 0.7 },
	{ label: '1–2', days: 1.5 },
	{ label: '2–3', days: 2.5 },
	{ label: '3–4', days: 3.5 },
	{ label: '≥ 4', days: 4.5 },
];

function projectHeatData(rows: ChartsData['byWeekProject']) {
	const projectsSet = new Set<string>();
	const weeksSet = new Set<string>();
	const matrix = new Map<string, number>();
	for (const r of rows) {
		projectsSet.add(r.projectName);
		weeksSet.add(r.weekStart);
		matrix.set(`${r.projectName}:${r.weekStart}`, r.hours / 8);
	}
	return {
		projects: [...projectsSet].sort(),
		weeks: [...weeksSet].sort(),
		matrix,
	};
}

/** Shared table renderer used for both the person matrix and the project matrix. */
function HeatTable({
	rowKeys,
	weeks,
	matrix,
	rowLabel,
}: {
	rowKeys: string[];
	weeks: string[];
	matrix: Map<string, number>;
	rowLabel: string;
}) {
	return (
		<table className={styles.heatTable}>
			<thead>
				<tr>
					<th className={styles.heatCorner} style={{ fontSize: 10, color: '#a3a3a3', textAlign: 'left' }}>
						{rowLabel}
					</th>
					{weeks.map((w) => (
						<th key={w} className={styles.heatTh}>
							<div className={styles.heatThInner}>{fmtWeek(w)}</div>
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rowKeys.map((key) => (
					<tr key={key}>
						<td className={styles.heatPerson} title={key}>
							{key}
						</td>
						{weeks.map((w) => {
							const d = matrix.get(`${key}:${w}`) ?? 0;
							return (
								<td
									key={w}
									className={styles.heatCell}
									style={{ background: heatBg(d), color: heatFg(d) }}
									title={`${key} · ${fmtWeek(w)} · ${fmtD(d)} d`}
								>
									{d > 0 ? fmtD(d) : ''}
								</td>
							);
						})}
					</tr>
				))}
			</tbody>
		</table>
	);
}

function HeatMatrix({ data }: { data: ChartsData }) {
	const personMx = useMemo(() => heatData(data.byPersonWeek), [data.byPersonWeek]);
	const projectMx = useMemo(() => projectHeatData(data.byWeekProject), [data.byWeekProject]);

	if (!personMx.persons.length) return <p className={styles.empty}>Aucune donnée pour cette période.</p>;

	// Use the union of weeks from both matrices so columns are aligned
	const allWeeks = useMemo(
		() => [...new Set([...personMx.weeks, ...projectMx.weeks])].sort(),
		[personMx.weeks, projectMx.weeks],
	);

	return (
		<div className={styles.heatWrap}>
			<div className={styles.heatScroll}>
				<div className={styles.heatSection}>
					<div className={styles.heatSectionTitle}>Par personne</div>
					<HeatTable
						rowKeys={personMx.persons}
						weeks={allWeeks}
						matrix={personMx.matrix}
						rowLabel="Personne"
					/>
				</div>

				<div className={styles.heatSection}>
					<div className={styles.heatSectionTitle}>Par projet</div>
					<HeatTable
						rowKeys={projectMx.projects}
						weeks={allWeeks}
						matrix={projectMx.matrix}
						rowLabel="Projet"
					/>
				</div>
			</div>

			<div className={styles.heatLegend}>
				<span className={styles.legendTitle}>Jours / semaine :</span>
				{LEGEND_STEPS.map((s) => (
					<span key={s.label} className={styles.legendItem}>
						<span className={styles.legendSwatch} style={{ background: heatBg(s.days) }} />
						{s.label}
					</span>
				))}
			</div>
		</div>
	);
}

// ── Page ──────────────────────────────────────────────────────────────────────
type Tab = 'dashboard' | 'heat';

export function ChartsPage() {
	const [tab, setTab] = useState<Tab>('dashboard');
	const [from, setFrom] = useState(threeMonthsAgo);
	const [to, setTo] = useState(todayStr);
	const [project, setProject] = useState(''); // selected project name, '' = all projects

	const { data, loading, error } = useCharts(from, to);

	// Full project list (independent of the current filter) used to populate the
	// selector, plus the project-filtered data fed to the charts.
	const allProjects = useMemo(
		() => (data ? [...new Set(data.byPersonProject.map((r) => r.projectName))].sort() : []),
		[data],
	);
	const viewData = useMemo(() => (data ? filterByProject(data, project) : null), [data, project]);

	return (
		<div className={styles.page}>
			<div className={styles.tabs}>
				<button
					className={tab === 'dashboard' ? styles.tabActive : styles.tab}
					onClick={() => setTab('dashboard')}
				>
					Tableau de bord
				</button>
				<button className={tab === 'heat' ? styles.tabActive : styles.tab} onClick={() => setTab('heat')}>
					Matrice d'intensité
				</button>
			</div>

			<div className={styles.controls}>
				<div className={styles.dateRow}>
					<label className={styles.dateLabel}>
						Du
						<input
							type="date"
							value={from}
							onChange={(e) => setFrom(e.target.value)}
							className={styles.dateInput}
						/>
					</label>
					<label className={styles.dateLabel}>
						Au
						<input
							type="date"
							value={to}
							onChange={(e) => setTo(e.target.value)}
							className={styles.dateInput}
						/>
					</label>
					<label className={styles.dateLabel}>
						Projet
						<select
							value={project}
							onChange={(e) => setProject(e.target.value)}
							className={styles.dateInput}
						>
							<option value="">Tous les projets</option>
							{allProjects.map((p) => (
								<option key={p} value={p}>
									{p}
								</option>
							))}
						</select>
					</label>
				</div>
			</div>

			{loading && <p className={styles.msg}>Chargement…</p>}
			{error && (
				<p className={styles.msg} style={{ color: '#dc2626' }}>
					{error}
				</p>
			)}

			{!loading &&
				!error &&
				viewData &&
				(tab === 'dashboard' ? <Dashboard data={viewData} /> : <HeatMatrix data={viewData} />)}
		</div>
	);
}
